import { DatePipe }                from '@angular/common';
import { Injectable }              from '@angular/core';
import {
	Tenant,
	TenantContractLog,
	TenantContractLogTMO
}                                  from '@app/modules/admin-tenant/models/tenant.model';
import {
	ElsterConfig,
	SystemConfig
}                                  from '@app/modules/configuration/models/configuration.model';
import { AdminTenantApiService }   from '@http/admin/admin-tenant-api.service';
import { SanitizerService }        from '@http/sanitizer.service';
import { AccountTenantApiService } from '@http/tenants/account-tenant-api.service';
import { ModelTypeEnum }           from '@models/transition.model';
import {
	ClsInitializationDTO,
	ClsTenantBodyDTO,
	ContractLogDTO,
	InitializationDTO,
	TenantTypeEnumDTO
}                                  from '@openApiGenerated/admin-api';
import {
	AccountInitializationDTO,
	AddressDataDTO,
	ClsAccountInitializationDTO,
	ClsAccountTenantBodyDTO
}                                  from '@openApiGenerated/management-api';
import { Address }                 from '@shared/components/address/models/address.model';
import { LoggerService }           from '@shared/services/logger.service';

@Injectable( {
	providedIn: 'root'
} )
export class TenantApiSanitizerService {

	constructor(
		protected logger: LoggerService,
		private readonly sanitize: SanitizerService,
		private readonly datePipe: DatePipe
	) {}


	// HELPERS ######################################
	private sanitizeTenantContractLogList(
		tenantContractLogList: TenantContractLogTMO[]
	): TenantContractLogTMO[] {
		return tenantContractLogList?.map( ( tenantContractLog: TenantContractLogTMO ) => {
			return this.sanitizeTenantContractLog( tenantContractLog );
		} );
	}

	private sanitizeTenantContractLog(
		tenantContractLogTMO: TenantContractLogTMO
	): TenantContractLogTMO {

		const typeTestValue: string | Date = tenantContractLogTMO?.from;

		if ( typeTestValue instanceof Date ) {
			return {
				...tenantContractLogTMO,
				from: tenantContractLogTMO?.from ? this.datePipe.transform( tenantContractLogTMO?.from as Date, 'yyyyMM' ) : null,
				to: tenantContractLogTMO?.to ? this.datePipe.transform( tenantContractLogTMO?.to as Date, 'yyyyMM' ) : null
			};

		} else if ( typeof typeTestValue === 'string' ) {

			// From
			const fromYearString = tenantContractLogTMO?.from as string;
			const fromYearNumber = parseInt( fromYearString?.slice( 0, 4 ) );
			const fromYear = !isNaN( fromYearNumber ) ? fromYearNumber : null;

			const fromMonthString = tenantContractLogTMO?.from as string;
			const fromMonthNumber = parseInt( fromMonthString?.slice( 4 ), 10 ) - 1;
			const fromMonth = !isNaN( fromMonthNumber ) ? fromMonthNumber : null;

			// To
			const toYearString = tenantContractLogTMO?.to as string;
			const toYearNumber = parseInt( toYearString?.slice( 0, 4 ), 10 );
			const toYear = !isNaN( toYearNumber ) ? toYearNumber : null;

			const toMonthString = tenantContractLogTMO?.to as string;
			const toMonthNumber = parseInt( toMonthString?.slice( 0, 4 ), 10 ) - 1;
			const toMonth = !isNaN( toMonthNumber ) ? toMonthNumber : null;

			return {
				...tenantContractLogTMO,
				from: Number.isInteger( fromYear ) && Number.isInteger( fromMonth ) ? new Date( fromYear, fromMonth ) : null,
				to: Number.isInteger( toYear ) && Number.isInteger( toMonth ) ? new Date( toYear, toMonth ) : null
			};
		}

		if ( tenantContractLogTMO ) {
			this.logger.warn( 'sanitizeTenantContractLog has no valid type to sanitize', tenantContractLogTMO );
		}
		return null;
	}

	// RESPONSE SANITIZER ######################################
	sanitizeApiResponse( entity: Tenant, context: AdminTenantApiService | AccountTenantApiService ): void {
		if ( entity instanceof Tenant ) {
			entity.billingAddress = context.sanitizer.sanitize.address( entity.billingAddress, ModelTypeEnum.TMO ) as Address;
			entity.createdDate = context.sanitizer.sanitize.date<Date>( entity.createdDate, ModelTypeEnum.TMO, Date );
			entity.lastModifiedDate = context.sanitizer.sanitize.date<Date>( entity.lastModifiedDate, ModelTypeEnum.TMO, Date );

			entity.archivingContractLog =
				context.sanitizer.sanitizeTenantContractLogList( entity.archivingContractLog ) as unknown as TenantContractLog[];

			entity.elsterContractLog =
				context.sanitizer.sanitizeTenantContractLogList( entity.elsterContractLog ) as unknown as TenantContractLog[];

			entity.fiscalContractLog = context.sanitizer.sanitizeTenantContractLogList( entity.fiscalContractLog ) as unknown as TenantContractLog[];
		}
	}

	sanitizeSystemConfigApiResponse( entity: SystemConfig, context: AdminTenantApiService | AccountTenantApiService ): void {
		context.sanitizer.sanitize.systemConfig( entity );
	}

	sanitizeElsterConfigApiResponse( entity: ElsterConfig, context: AdminTenantApiService | AccountTenantApiService ): void {
		context.sanitizer.sanitize.elsterConfig( entity );
	}

	// REQUEST SANITIZER ######################################
	sanitizeForAdminApiRequest( dto: InitializationDTO | ClsTenantBodyDTO, context: AdminTenantApiService | AccountTenantApiService ): void {
		if ( dto instanceof ClsInitializationDTO || dto instanceof ClsTenantBodyDTO ) {
			dto.billingAddress = context.sanitizer.sanitize.address( dto.billingAddress, ModelTypeEnum.DTO ) as AddressDataDTO;
			dto.tenantType = dto?.tenantType ? dto.tenantType : TenantTypeEnumDTO.STANDARD;

			dto.archivingContract = context.sanitizer.sanitizeTenantContractLog( dto.archivingContract ) as unknown as ContractLogDTO;
			dto.elsterContract = context.sanitizer.sanitizeTenantContractLog( dto.elsterContract ) as unknown as ContractLogDTO;
			dto.fiscalContract = context.sanitizer.sanitizeTenantContractLog( dto.fiscalContract ) as unknown as ContractLogDTO;
		}
	}

	sanitizeForAccountApiRequest( dto: AccountInitializationDTO | ClsAccountTenantBodyDTO, context: AdminTenantApiService | AccountTenantApiService ): void {
		if ( dto instanceof ClsAccountInitializationDTO || dto instanceof ClsAccountTenantBodyDTO ) {
			dto.billingAddress = context.sanitizer.sanitize.address( dto.billingAddress, ModelTypeEnum.DTO ) as AddressDataDTO;

			dto.archivingContract = context.sanitizer.sanitizeTenantContractLog( dto.archivingContract ) as unknown as ContractLogDTO;
			dto.fiscalContract = context.sanitizer.sanitizeTenantContractLog( dto.fiscalContract ) as unknown as ContractLogDTO;
		}
	}
}
